import { z } from "zod";
import { I18nTextSchema } from "./store.ts";

/**
 * What a meal set is made of (`ITEM_MGMT_SET_MEAL`,
 * `09-reports.md` § 2 — "Meal sets expand into sub-items").
 *
 * A set is a menu item like any other, with groups of components hanging off
 * it: "choose a main", "choose a drink". The group carries how many may be
 * taken, the option carries what taking it adds to the price — a set that
 * lets a diner upgrade to the steak is the ordinary case, not the exotic one.
 *
 * An option points at another menu item rather than naming one, because a
 * meal set's components have to be attributable in the item report and a
 * name is not an attribution.
 */

const Money = z.string().regex(/^-?\d+$/, "minor units, as digits");

export const MealSetOptionSchema = z.object({
  itemId: z.uuid(),
  name: z.string(),
  /** What choosing this adds to the set's price. Signed: a downgrade is real. */
  priceDelta: Money,
  sort: z.number().int(),
});

export const MealSetGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  minSelectable: z.number().int(),
  maxSelectable: z.number().int(),
  sort: z.number().int(),
  /**
   * The group only. "Choose a side" is the merchant's own wording and needs
   * translating; an option is a menu item and reads in whatever language that
   * item was given.
   */
  i18n: I18nTextSchema,
  options: z.array(MealSetOptionSchema),
});
export type MealSetGroup = z.infer<typeof MealSetGroupSchema>;

export const SaveMealSetSchema = z.object({
  groups: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        minSelectable: z.number().int().min(0).max(20).default(1),
        maxSelectable: z.number().int().min(1).max(20).default(1),
        // The save rewrites every group, so its translations travel with it —
        // the id they hang off does not survive the request.
        i18n: I18nTextSchema.default({}),
        options: z
          .array(
            z.object({
              itemId: z.uuid(),
              priceDelta: Money.default("0"),
            }),
          )
          .max(50),
      }),
    )
    .max(20)
    .default([]),
});
